// TODO: vitest component test (pages are covered by Playwright e2e)
import { useParcelExtractionEnabled } from '@/hooks/useParcelExtractionEnabled'
import { useState, useRef } from 'react'
import { useNavigate, Navigate } from 'react-router'
import { useTranslation } from 'react-i18next'
import { Camera } from 'lucide-react'
import { PageHeader, Breadcrumbs } from '@/components/layout'
import { Button, Card, Select } from '@/components/ui'
import { useCreateParcel } from '@/hooks/useParcels'
import { useProperties } from '@/hooks/useProperties'
import { useLocalizationConfig } from '@/hooks/useDateFormatter'
import { localToday } from '@/lib/date'
import { api } from '@/lib/api'
import { useNotificationStore } from '@/stores/useNotificationStore'
import { useApiErrorTranslator } from '@/hooks/useApiErrorTranslator'

// ---------------------------------------------------------------------------
// Image upload helper — same pattern as ParcelFormPage
// ---------------------------------------------------------------------------

async function uploadParcelImage(file: File): Promise<string> {
  const { data: presigned } = await api.post<{ url: string; key: string }>('/api/presigned_urls', {
    content_type: file.type,
  })
  await fetch(presigned.url, {
    method: 'PUT',
    body: file,
    headers: { 'Content-Type': file.type },
  })
  return `/api/images/${presigned.key}`
}

// ---------------------------------------------------------------------------
// Outer component — flag gate only; all real hooks called in the inner
// component so they are unconditional.
// ---------------------------------------------------------------------------

export function ScanParcelPage() {
  const parcelTextExtractionEnabled = useParcelExtractionEnabled()

  if (!parcelTextExtractionEnabled) {
    return <Navigate to="/parcels" replace />
  }

  return <ScanParcelPageContent />
}

// ---------------------------------------------------------------------------
// Inner component — "snap & save" quick capture.
//
// EP-67 redesign: no per-parcel scan wizard. Pick a property + photo and save;
// the parcel is created immediately and extraction runs in the BACKGROUND (the
// model auto-enqueues on create when image_urls are present). Anything that
// can't auto-resolve a unit surfaces in the list's "Needs review" tab for batch
// resolution — the capturing staff member is never blocked waiting for OCR.
// ---------------------------------------------------------------------------

function ScanParcelPageContent() {
  const { t } = useTranslation('parcels')
  const navigate = useNavigate()
  const addToast = useNotificationStore((s) => s.addToast)
  const translateError = useApiErrorTranslator('parcels')

  const [imageFile, setImageFile] = useState<File | null>(null)
  const [imagePreviewUrl, setImagePreviewUrl] = useState<string | null>(null)
  const [selectedPropertyId, setSelectedPropertyId] = useState('')
  const [isSaving, setIsSaving] = useState(false)

  const fileInputRef = useRef<HTMLInputElement>(null)

  const createParcel = useCreateParcel()
  const { data: propertiesData } = useProperties(1)
  const { timezone } = useLocalizationConfig()

  const properties = propertiesData?.data ?? []
  const propertyOptions = [
    { value: '', label: t('placeholders.select_property') },
    ...properties.map((p) => ({ value: String(p.id), label: p.name })),
  ]

  const canSave = !!selectedPropertyId && !!imageFile

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    setImageFile(file)
    setImagePreviewUrl(URL.createObjectURL(file))
  }

  const handleQuickAdd = async () => {
    if (!canSave || !imageFile) return
    setIsSaving(true)
    try {
      const imageUrl = await uploadParcelImage(imageFile)
      await createParcel.mutateAsync({
        property_id: Number(selectedPropertyId),
        image_urls: [imageUrl],
        received_at: localToday(timezone),
      })
      addToast({ type: 'success', message: t('messages.created') })
      navigate('/parcels')
    } catch (err) {
      addToast({ type: 'error', message: translateError(err) })
      setIsSaving(false)
    }
  }

  return (
    <div className="space-y-6">
      <Breadcrumbs
        items={[
          { label: t('title'), href: '/parcels' },
          { label: t('scan.page_title') },
        ]}
      />

      <PageHeader title={t('scan.page_title')} />

      <Card>
        <div className="space-y-6">
          <div className="grid gap-6 md:grid-cols-2">
            <Select
              label={t('labels.property')}
              required
              options={propertyOptions}
              value={selectedPropertyId}
              onChange={(e) => setSelectedPropertyId(e.target.value)}
            />

            {/* Photo capture */}
            <div className="space-y-2">
              <label className="block text-sm font-medium">{t('labels.photos')}</label>

              <label
                htmlFor="scan-photo-input"
                className="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-base-300 rounded-lg p-6 cursor-pointer hover:border-primary transition-colors text-center"
                data-testid="photo-capture-label"
              >
                <Camera className="w-8 h-8 text-base-content/40" aria-hidden="true" />
                <span className="text-sm font-medium">{t('scan.capture_prompt')}</span>
                <input
                  id="scan-photo-input"
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  capture="environment"
                  className="hidden"
                  onChange={handleFileChange}
                  data-testid="photo-input"
                />
              </label>

              <p className="text-xs text-base-content/50 text-center">{t('scan.or_upload')}</p>

              {imagePreviewUrl && (
                <div className="rounded-lg overflow-hidden border border-base-200">
                  <img
                    src={imagePreviewUrl}
                    alt={t('scan.photo_preview')}
                    className="w-full max-h-40 object-contain bg-base-200"
                  />
                </div>
              )}

              <p className="text-xs text-base-content/50" data-testid="extraction-photo-hint">
                {t('extraction.photo_hint')}
              </p>
            </div>
          </div>

          <div className="flex justify-end">
            <Button
              onClick={handleQuickAdd}
              disabled={!canSave}
              loading={isSaving || createParcel.isPending}
              data-testid="cta-quick-add"
            >
              {t('scan.quick_add')}
            </Button>
          </div>
        </div>
      </Card>
    </div>
  )
}
